import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Visible label (renders to the right of the box). */
  label?: ReactNode;
  /** Optional helper/hint text beneath the row. */
  hint?: ReactNode;
  /** Error message; when set, renders the row + box in the danger state. */
  errorMessage?: ReactNode;
  /** Three-state checkbox (e.g. "select all" with partial). When true the box renders the indeterminate glyph. */
  indeterminate?: boolean;
}

/**
 * Square checkbox + clickable label row. Matches Doctrine design system:
 *  - 16×16 box, r2 radius, brand-color on checked, white check glyph
 *  - Focus ring: 3px brand@20% (or danger@20% on error)
 *  - States: default, hover, focus, checked, indeterminate, error, disabled
 *
 * Wraps a native `<input type="checkbox">` so `checked`, `onChange`, form
 * submission, autocomplete, etc. all just work.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, errorMessage, indeterminate = false, className, id, disabled, checked, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = errorMessage || hint ? `${inputId}-message` : undefined;

  // Forward the `indeterminate` flag to the DOM element (it's not a normal HTML
  // attribute — must be set via the DOM property).
  const setIndeterminate = (node: HTMLInputElement | null) => {
    if (node) node.indeterminate = indeterminate;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
  };

  return (
    <div
      className={[
        styles.wrapper,
        errorMessage ? styles.wrapperError : null,
        disabled ? styles.wrapperDisabled : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <label className={styles.row} htmlFor={inputId}>
        <span className={styles.boxContainer}>
          <input
            ref={setIndeterminate}
            id={inputId}
            type="checkbox"
            className={styles.input}
            disabled={disabled}
            checked={checked}
            aria-invalid={errorMessage ? true : undefined}
            aria-describedby={messageId}
            {...rest}
          />
          <span className={styles.box} aria-hidden="true">
            {indeterminate ? <IndeterminateGlyph /> : <CheckGlyph />}
          </span>
        </span>
        {label && <span className={styles.label}>{label}</span>}
      </label>

      {errorMessage && (
        <p id={messageId} className={[styles.message, styles.messageError].join(' ')} role="alert">
          {errorMessage}
        </p>
      )}
      {!errorMessage && hint && (
        <p id={messageId} className={[styles.message, styles.messageHint].join(' ')}>
          {hint}
        </p>
      )}
    </div>
  );
});

function CheckGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6.2 L5 8.5 L9.5 3.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IndeterminateGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M3 6 H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
